import { useInfiniteQuery } from '@tanstack/react-query';

import { dashboardV2Client } from '../api/client';
import { dashboardV2QueryKeys } from '../api/queryKeys';
import { ShiftActivityData } from '../api/types';

// One page is comfortably more than a shift's usual history, so the Load more
// control stays rare; the endpoint caps per_page at 100 regardless.
export const ACTIVITY_PAGE_SIZE = 25;

// Full activity history for the "View all" modal (GRAVY-10294). Paged rather
// than fetched whole: a long-running shift can accumulate an unbounded number
// of events, and the modal must open on the first page instead of waiting for
// all of them.
//
// `enabled` is the modal's open state — the history is never requested while
// the panel is merely open, only when the admin asks for it.
const useShiftActivity = (
  agencyId: number,
  shiftId: number,
  enabled: boolean,
) =>
  useInfiniteQuery<ShiftActivityData>({
    queryKey: dashboardV2QueryKeys.shiftActivity(agencyId, shiftId),
    queryFn: ({ pageParam }) =>
      dashboardV2Client.getShiftActivity(
        agencyId,
        shiftId,
        pageParam as number,
        ACTIVITY_PAGE_SIZE,
      ),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.pagination.page < last.pagination.total_pages
        ? last.pagination.page + 1
        : undefined,
    enabled: enabled && agencyId != null && shiftId != null,
    // Activity is append-only and the modal is short-lived, so a reopen inside
    // the window reuses what was already fetched instead of refetching every
    // page the admin had loaded.
    staleTime: 15_000,
  });

export default useShiftActivity;
